export type CompatiblePlatform = {
  id: string;
  name: string;
  code: string;
};

export type CompatibleGame = {
  platform_id: string;
  compatible_platforms?: string | null;
};

type VisibleMachinePlatform = CompatiblePlatform & {
  ownedCount: number;
  machineCount: number;
  directOwnedCount: number;
};

function compact(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('fr')
    .replace(/[^a-z0-9]+/g, '');
}

export function isVisibleMachinePlatform(platform: VisibleMachinePlatform): boolean {
  const normalizedName = platform.name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('fr');
  const genericFamilies = new Set([
    'oric',
    'spectrum',
    'zx spectrum',
    'amstrad cpc',
    'msx',
    'msx2',
  ]);
  return (
    !genericFamilies.has(normalizedName) &&
    platform.ownedCount > 0 &&
    (platform.directOwnedCount > 0 || platform.machineCount > 0)
  );
}

export function compareMachinePlatforms(
  left: Pick<CompatiblePlatform, 'name'>,
  right: Pick<CompatiblePlatform, 'name'>,
): number {
  return left.name.localeCompare(right.name, 'fr', { sensitivity: 'base' });
}

export function machineCompatibilityName(platform: CompatiblePlatform): string | null {
  const key = compact(`${platform.name} ${platform.code}`);
  if (/cpc6128/.test(key)) return 'CPC6128';
  if (/cpc464/.test(key)) return 'CPC464';
  if (/spectrum(?:plus)?2/.test(key)) return 'ZX Spectrum +2';
  if (/spectrum48k/.test(key)) return 'ZX Spectrum 48K';
  if (/spectrumplus/.test(key)) return 'ZX Spectrum +';
  if (/zxspectrum|spectrum/.test(key)) return 'ZX Spectrum';
  if (/oricatmos/.test(key)) return 'Oric Atmos';
  if (/oric1/.test(key)) return 'Oric 1';
  if (/thomsonmo6|\bmo6\b/.test(`${platform.name.toLocaleLowerCase('fr')} ${platform.code}`)) {
    return 'MO6';
  }
  if (/thomsonmo5|\bmo5\b/.test(`${platform.name.toLocaleLowerCase('fr')} ${platform.code}`)) {
    return 'MO5';
  }
  if (/to770/.test(key)) return 'TO7/70';
  if (/thomsonto8|\bto8\b/.test(`${platform.name.toLocaleLowerCase('fr')} ${platform.code}`)) {
    return 'TO8';
  }
  const name = platform.name.trim();
  if (/^msx\s*[-–—]/i.test(name)) return name;
  return null;
}

export function gameBelongsToPlatform(game: CompatibleGame, platform: CompatiblePlatform): boolean {
  if (game.platform_id === platform.id) return true;
  const machine = machineCompatibilityName(platform);
  if (!machine) return false;
  return String(game.compatible_platforms ?? '')
    .split(',')
    .some((value) => value.trim().localeCompare(machine, 'fr', { sensitivity: 'base' }) === 0);
}
